"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck, ScrollText, ShieldCheck, Landmark, CircleCheck as CheckCircle2, ChevronRight, Download } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface LegalPlacard {
  id: string;
  badge: string;
  decisionNumber: string;
  authority: string;
  date: string;
  title: string;
  description: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  isPrimary?: boolean;
}
const placards: LegalPlacard[] = [{
  id: "placard-1",
  badge: "Quyết định thành lập",
  decisionNumber: "Quyết định 483/QĐ-BNV ngày 02/4/2021",
  authority: "Bộ Nội vụ",
  date: "02/04/2021",
  title: "Cho phép thành lập Hội Địa lý môi trường Việt Nam",
  description: "Văn bản chính thức của Bộ Nội vụ công nhận việc thành lập Hội Địa lý môi trường Việt Nam (VAEG) với tư cách là tổ chức xã hội - nghề nghiệp hoạt động trên phạm vi toàn quốc.",
  bullet1: "Xác lập tư cách pháp nhân chính thức của Hội",
  bullet2: "Tập hợp các nhà khoa học, chuyên gia địa lý môi trường",
  bullet3: "Triển khai hoạt động nghiên cứu khoa học trên toàn quốc",
  isPrimary: true
}, {
  id: "placard-2",
  badge: "Phê duyệt điều lệ",
  decisionNumber: "Quyết định 1157/QĐ-BNV ngày 03/11/2021",
  authority: "Bộ Nội vụ",
  date: "03/11/2021",
  title: "Phê duyệt Điều lệ Hội Địa lý môi trường Việt Nam",
  description: "Quyết định chuẩn y toàn văn Điều lệ tổ chức và hoạt động của Hội, định hình hệ thống quyền hạn, trách nhiệm hội viên, cơ cấu đại hội và ban chấp hành trung ương.",
  bullet1: "Quy định tôn chỉ, mục đích và phạm vi hoạt động",
  bullet2: "Xác lập tiêu chuẩn, quyền hạn và nghĩa vụ hội viên",
  bullet3: "Quy chế quản lý tài chính và tài sản minh bạch",
  isPrimary: false
}, {
  id: "placard-3",
  badge: "Địa vị pháp lý & Quản lý",
  decisionNumber: "Tư cách pháp nhân độc lập",
  authority: "Bộ Nội vụ & Bộ TN&MT",
  date: "Hiện hành",
  title: "Tư cách pháp nhân và cơ chế quản lý nhà nước",
  description: "Hội có tư cách pháp nhân độc lập, con dấu và biểu tượng riêng, mở tài khoản tại ngân hàng và Kho bạc Nhà nước theo luật định; chịu sự quản lý nhà nước của các bộ chuyên ngành.",
  bullet1: "Có con dấu riêng, biểu tượng và tài khoản độc lập",
  bullet2: "Chịu sự quản lý nhà nước về tổ chức của Bộ Nội vụ",
  bullet3: "Chịu sự quản lý chuyên ngành của các Bộ, ngành liên quan",
  isPrimary: false
}];
export default function Legal() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="cơ-sở-pháp-lý" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      {/* Background Cartographic Vector Geometry Motifs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] select-none">
        <svg className="w-full h-full text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M-100,200 C150,150 250,300 450,220 C650,140 750,280 950,230" />
          <path d="M-100,260 C180,210 280,360 480,280 C680,200 780,340 980,290" />
          <path d="M-100,320 C200,270 300,420 500,340 C700,260 800,400 1000,350" />
          <path d="M-100,380 C220,330 320,480 520,400 C720,320 820,460 1020,410" />
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Institutional Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm border border-primary/25 bg-accent/60 text-primary text-xs uppercase tracking-wider font-semibold">
            <ShieldCheck className="size-4" />
            <span>Tính pháp lý và thẩm quyền</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-secondary font-default leading-tight">
            Cơ sở pháp lý
          </h2>

          {/* Institutional Anchor Divider */}
          <div className="mt-4 mb-5 flex items-center">
            <div className="h-[3px] w-12 bg-secondary rounded-xs" />
            <div className="h-[1px] flex-1 bg-border" />
          </div>

          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Hội Địa lý môi trường Việt Nam hoạt động tuân thủ Hiến pháp, pháp luật nước Cộng hòa Xã hội Chủ nghĩa Việt Nam và Điều lệ Hội được Bộ Nội vụ phê duyệt.
          </p>
        </div>

        {/* 3 Structured Decision Placards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {placards.map((placard, i) => <motion.div key={placard.id} variants={itemVariants} data-index={i} className="h-full flex">
              <Card className={`relative flex flex-col justify-between w-full rounded-sm border bg-card p-6 sm:p-7 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 ${placard.isPrimary ? "border-primary/40 border-t-[3px] border-t-primary" : "border-border border-t-[3px] border-t-primary/70"}`}>
                <div>
                  <CardHeader className="p-0 mb-5 gap-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium bg-muted text-foreground border border-border">
                        {i === 0 && <FileCheck className="size-3.5 text-primary" />}
                        {i === 1 && <ScrollText className="size-3.5 text-secondary" />}
                        {i === 2 && <Landmark className="size-3.5 text-primary" />}
                        {placard.badge}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {placard.date}
                      </span>
                    </div>

                    <div className="mt-2">
                      <div className="text-xs font-semibold text-primary uppercase tracking-wide">
                        {placard.authority}
                      </div>
                      <CardTitle className="text-lg sm:text-xl font-semibold text-foreground mt-1 leading-snug font-default">
                        {placard.decisionNumber}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="p-0 space-y-4">
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {placard.title}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {placard.description}
                    </p>

                    <div className="pt-3 border-t border-border/80">
                      <div className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2.5">
                        Điểm cốt lõi
                      </div>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{placard.bullet1}</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{placard.bullet2}</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{placard.bullet3}</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <div className="pt-6 mt-6 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <Link to="https://hoidialymoitruong.vn/wp-content/uploads/2026/09/Ho-So-Hoi-Dia-Ly-Moi-Truong-Viet-Nam.pdf" newTab className="inline-flex items-center gap-1.5 transition-colors text-[#01724C] hover:text-[#009EE2]">
                      <Download size={14} />
                      <span>{i === 2 ? "Xem trong Điều lệ Hội" : "Tải bản PDF"}</span>
                    </Link>
                    <span className="inline-flex items-center text-primary font-medium group cursor-default">
                      Văn bản hiệu lực
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Administrative Note & Navigation Strip */}
        <div className="mt-12 p-5 sm:p-6 rounded-sm bg-muted/70 border border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1 max-w-3xl">
            <h3 className="text-sm font-semibold text-foreground font-default">
              Tra cứu văn bản và điều lệ
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Tài liệu các văn bản pháp lý chính thức của Hội được công khai để tải về ngay trên trang này. Hội viên và các tổ chức đối tác cần tra cứu thêm các văn bản khác vui lòng liên hệ Văn phòng Trung ương Hội để được hướng dẫn.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="default" size="sm" className="rounded-sm font-medium">
              <Link to="/ContactPage">
                <span>Liên hệ văn phòng</span>
                <ChevronRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-sm font-medium">
              <Link to="/OrganizationStructurePage">
                <span>Cơ cấu tổ chức</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}